/**
 * Marketing tab tiles (quick-261104-ia3, John 2026-11-04: "every Marketing tile
 * must be trended over time"). Lead and pipeline mixes are monthly stacked
 * columns over the last 12 months (top 5 + Other); channel conversion, win rate,
 * cycle and days-to-win are quarterly lines. Data: GET /api/main/exec →
 * panels.marketing. MAIN-33 / MAIN-49 were already trends and stay GroupedTiles.
 */
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';

import { monthLabel } from '../../lib/exec/fmt.js';
import { todayIso } from '../../lib/exec/periods.js';
import {
  fmtLine,
  fmtStack,
  isEmptyLines,
  isEmptyStack,
  latestPoint,
  legendItems,
  monthlyStackOption,
  quarterLinesOption,
  stackSummary,
  type LineUnit,
  type StackUnit,
} from '../../lib/exec/trend-charts.js';

import { DashTile, Legend, TileEmpty } from './DashTile.js';

import type { ExecMarketingPanel, ExecMonthlyStack, ExecQuarterLines } from '@saledash/shared';

type StackKey = 'leadsByStatus' | 'leadsBySource' | 'leadsByIndustry' | 'workingByOwner' | 'disqualByReason' | 'pipelineByChannel';
type LinesKey = 'leadToOppByChannel' | 'winRateByChannel' | 'cycleByChannel' | 'daysToWin';

interface StackSpec {
  key: StackKey;
  title: string;
  info: string;
  unit: StackUnit;
  noun: string;
  footer: string;
  note: string;
  testId: string;
}

interface LinesSpec {
  key: LinesKey;
  title: string;
  info: string;
  unit: LineUnit;
  sub: string;
  footer: string;
  note: string;
  testId: string;
}

export const MARKETING_STACK_TILES: Record<StackKey, StackSpec> = {
  leadsByStatus: {
    key: 'leadsByStatus',
    title: 'Inbound leads by status',
    info: 'New inbound leads each month for the last year, split by where each lead stands today: new, working, qualified, disqualified and so on.',
    unit: 'count',
    noun: 'leads',
    footer: 'Salesforce inbound leads by created month · top 5 statuses + Other',
    note: 'Leads created this month, by status today.',
    testId: 'mkt-leads-status',
  },
  leadsBySource: {
    key: 'leadsBySource',
    title: 'Inbound leads by source',
    info: 'New inbound leads each month, split by where they came from: website, events, partners, paid campaigns and so on.',
    unit: 'count',
    noun: 'leads',
    footer: 'Salesforce Lead Source · top 5 sources + Other',
    note: 'Leads created this month, by source.',
    testId: 'mkt-leads-source',
  },
  leadsByIndustry: {
    key: 'leadsByIndustry',
    title: 'Inbound leads by industry',
    info: 'New inbound leads each month, split by industry. Shows whether marketing is attracting our best-fit markets, and how that mix is shifting.',
    unit: 'count',
    noun: 'leads',
    footer: 'Salesforce lead industry · top 5 industries + Other',
    note: 'Leads created this month, by industry.',
    testId: 'mkt-leads-industry',
  },
  disqualByReason: {
    key: 'disqualByReason',
    title: 'Disqualified leads by reason',
    info: 'Inbound leads ruled out, by the month they came in and the reason given. A growing "not a fit" share points to audience targeting; "no response" points to follow-up timing.',
    unit: 'count',
    noun: 'leads',
    footer: 'Salesforce disqualification reason · top 5 reasons + Other',
    note: 'Disqualified leads created this month, by reason.',
    testId: 'mkt-disqual',
  },
  workingByOwner: {
    key: 'workingByOwner',
    title: 'Working leads by owner',
    info: 'Inbound leads someone is actively working, by the month the lead came in and who owns it. Shows how follow-up work is spread across the team over time.',
    unit: 'count',
    noun: 'leads',
    footer: 'Salesforce inbound leads in a working status · top 5 owners + Other',
    note: 'Leads created this month that are being worked, by owner.',
    testId: 'mkt-working-owner',
  },
  pipelineByChannel: {
    key: 'pipelineByChannel',
    title: 'Marketing pipeline by channel',
    info: 'Dollars of new pipeline that marketing sourced each month, split by the channel that brought the lead in.',
    unit: 'money',
    noun: 'created',
    footer: 'Marketing-sourced product opportunities by created month · top 5 channels + Other',
    note: 'Pipeline dollars created this month, by channel.',
    testId: 'mkt-pipeline-channel',
  },
};

export const MARKETING_LINE_TILES: Record<LinesKey, LinesSpec> = {
  leadToOppByChannel: {
    key: 'leadToOppByChannel',
    title: 'Lead to opportunity by channel',
    info: 'Each quarter, the share of leads from each marketing channel that turned into an opportunity. Higher means the channel brings better leads. Hover to see how many leads sit behind each point.',
    unit: 'percent',
    sub: 'leads that became opps, by quarter',
    footer: 'By lead channel · Outbound and ZoomInfo (BDR / sales) left out · gaps mean no leads that quarter',
    note: 'n = leads from the channel that quarter.',
    testId: 'mkt-lead-opp',
  },
  winRateByChannel: {
    key: 'winRateByChannel',
    title: 'Win rate by channel',
    info: 'Each quarter, of marketing-sourced deals that finished, the share we won, by channel. Small channels swing a lot, so check the deal count on hover.',
    unit: 'percent',
    sub: 'won ÷ (won + lost), by close quarter',
    footer: 'Marketing channels only, Outbound and ZoomInfo left out · gaps mean none closed',
    note: 'n = deals that finished (won or lost) that quarter.',
    testId: 'mkt-winrate-channel',
  },
  cycleByChannel: {
    key: 'cycleByChannel',
    title: 'Sales cycle by channel',
    info: 'Each quarter, the average days from opportunity created to won for marketing-sourced deals, by channel. Shorter is better.',
    unit: 'days',
    sub: 'avg days to win, by close quarter',
    footer: 'Won marketing-sourced deals by channel · gaps mean no wins',
    note: 'n = deals won that quarter.',
    testId: 'mkt-cycle-channel',
  },
  daysToWin: {
    key: 'daysToWin',
    title: 'Days to win: marketing vs other',
    info: 'Each quarter, the average days from opportunity created to won, comparing marketing-sourced deals with all other deals. Shorter is better.',
    unit: 'days',
    sub: 'avg days to win, by close quarter',
    footer: 'Won product deals · created → close',
    note: 'n = deals won that quarter.',
    testId: 'mkt-days-to-win',
  },
};

/** Shared empty/loading body so a missing panel never crashes a tile. */
function emptyText(loading: boolean, present: boolean, emptyMsg: string): string {
  if (!present) return loading ? 'Loading…' : 'Data refreshing — this chart fills in after the next refresh.';
  return emptyMsg;
}

export function MarketingStackTile({
  spec,
  marketing,
  loading = false,
}: {
  spec: StackSpec;
  marketing: ExecMarketingPanel | undefined;
  loading?: boolean;
}): React.JSX.Element {
  const stack: ExecMonthlyStack | undefined = marketing?.[spec.key];
  const empty = isEmptyStack(stack);
  const option = useMemo(() => (stack && !empty ? monthlyStackOption(stack, { unit: spec.unit, note: spec.note }) : null), [stack, empty, spec]);
  const sum = stackSummary(stack);
  const current = sum?.lastMonth != null && sum.lastMonth === todayIso().slice(0, 7);
  const sub =
    sum?.lastMonth
      ? `${spec.noun}, last 12 months · ${monthLabel(sum.lastMonth)}${current ? ' so far' : ''}: ${fmtStack(spec.unit, sum.lastValue)}`
      : `${spec.noun}, last 12 months`;
  return (
    <DashTile
      title={spec.title}
      info={spec.info}
      headline={sum ? fmtStack(spec.unit, sum.total) : '—'}
      sub={sub}
      size="lg"
      footer={spec.footer}
      testId={spec.testId}
    >
      {!stack || empty || !option ? (
        <TileEmpty text={emptyText(loading, !!stack, 'Nothing recorded in the last 12 months.')} />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={legendItems(stack.series)} />
        </div>
      )}
    </DashTile>
  );
}

export function MarketingLinesTile({
  spec,
  marketing,
  loading = false,
}: {
  spec: LinesSpec;
  marketing: ExecMarketingPanel | undefined;
  loading?: boolean;
}): React.JSX.Element {
  const lines: ExecQuarterLines | undefined = marketing?.[spec.key];
  const empty = isEmptyLines(lines);
  const option = useMemo(() => (lines && !empty ? quarterLinesOption(lines, { unit: spec.unit, note: spec.note }) : null), [lines, empty, spec]);
  let headline: string | undefined;
  let sub = spec.sub;
  if (spec.key === 'daysToWin') {
    const mk = latestPoint(lines, 'Marketing');
    const other = latestPoint(lines, 'Other');
    headline = mk ? fmtLine('days', mk.value) : '—';
    sub = mk ? `marketing, ${mk.quarter}${other ? ` · other: ${fmtLine('days', other.value)}` : ''}` : spec.sub;
  }
  return (
    <DashTile title={spec.title} info={spec.info} headline={headline} sub={sub} size="lg" footer={spec.footer} testId={spec.testId}>
      {!lines || empty || !option ? (
        <TileEmpty text={emptyText(loading, !!lines, 'No quarters with data yet.')} />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={legendItems(lines.series)} />
        </div>
      )}
    </DashTile>
  );
}
